import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { App, Button, Card, Col, DatePicker, Empty, List, Row, Statistic, Table, Tabs, Tag, Tooltip, theme } from "antd";
import { Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";
import type { DashboardStats } from "@shoe-trace/shared";
import {
  fetchDashboard,
  fetchIntercepts,
  listReconciliations,
  fetchTodos,
  fetchWorkload,
  type InterceptReport,
  type ReconciliationRow,
  type TodoCounts,
  type WorkloadReport,
} from "../api/client";
import { formatWhen, LoadFail, PageSpin } from "../components/widgets";
import { canManage, useSession } from "../session";

const PIE_COLORS = ["#1677ff", "#52c41a", "#faad14", "#eb2f96", "#13c2c2"];

export function Dashboard(): JSX.Element {
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { user } = useSession();
  const navigate = useNavigate();
  const manager = canManage(user);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [todos, setTodos] = useState<TodoCounts | null>(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [intercepts, setIntercepts] = useState<InterceptReport[]>([]);
  const [workload, setWorkload] = useState<WorkloadReport[]>([]);
  const [reconciliations, setReconciliations] = useState<ReconciliationRow[]>([]);
  const [range, setRange] = useState<[string, string] | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    void Promise.all([fetchDashboard(), fetchTodos()])
      .then(([nextStats, nextTodos]) => {
        setStats(nextStats);
        setTodos(nextTodos);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadReports = useCallback((from?: string, to?: string) => {
    if (!manager) return;
    void Promise.all([fetchIntercepts(from, to), fetchWorkload(from, to), listReconciliations()])
      .then(([blocked, work, recon]) => {
        setIntercepts(blocked);
        setWorkload(work);
        setReconciliations(recon);
        if (from || to) message.success("已按所选日期重新汇总。");
      })
      .catch(() => setError(true));
  }, [manager, message]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  if (loading) return <PageSpin tip="正在汇总今天的工作和库存…" />;
  if (error || !stats || !todos) return <LoadFail title="总览没有加载出来。" onRetry={load} />;

  const todoItems = [
    { title: "待打印", count: todos.printTasks, path: "/tag-station", show: true },
    { title: "待上架", count: todos.unlocated, path: "/locations", show: true },
    { title: "待复核", count: todos.remaining, path: "/packing", show: true },
    { title: "待对账", count: todos.reconcile, path: "/reconciliation", show: manager },
    {
      title: manager ? "等你通过" : "我提交的还在等",
      count: todos.pendingApprovals,
      path: "/exceptions",
      show: true,
    },
  ].filter((item) => item.show);

  const emptyTodos = todoItems.every((item) => item.count === 0);

  return (
    <Tabs
      items={[
        {
          key: "todo",
          label: "待办",
          children: emptyTodos ? (
            <Empty description="今天没有待办。有到货就去 SKU 建档导入到货单，再去赋码打印。" />
          ) : (
            <List
              dataSource={todoItems}
              renderItem={(item) => (
                <List.Item style={{ cursor: "pointer" }} onClick={() => navigate(item.path)}>
                  <TypographyLine title={item.title} count={item.count} />
                </List.Item>
              )}
            />
          ),
        },
        {
          key: "stock",
          label: "库存概览",
          children: (
            <>
              <Row gutter={16}>
                <Col span={8}><Card><Statistic title={<Tooltip title="含待上架。已经扫过入库、还没出库的双数。"><span>已入库</span></Tooltip>} value={stats.inStockCount} suffix="双" /></Card></Col>
                <Col span={8}><Card><Statistic title="今日复核" value={stats.packedToday} suffix="双" valueStyle={{ color: token.colorSuccess }} /></Card></Col>
                <Col span={8}><Card><Statistic title="今日入库" value={stats.activatedToday} suffix="双" valueStyle={{ color: token.colorPrimary }} /></Card></Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={14}>
                  <Card title={<Tooltip title="含待上架。按批次看已经入库、还没出库的双数。"><span>批次库龄分布（已入库双数）</span></Tooltip>}>
                    {stats.batchDistribution.length === 0 ? (
                      <Empty description="还没有已入库的批次。扫码入库后，这里会按批次显示库龄。" />
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <BarChart data={stats.batchDistribution}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="batchNo" />
                          <YAxis />
                          <ChartTooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.[0]) return null;
                              const row = payload[0].payload as DashboardStats["batchDistribution"][number];
                              return <div style={{ background: "#fff", padding: 8 }}>{row.inStockQty} 双 · 库龄 {row.ageDays} 天</div>;
                            }}
                          />
                          <Bar dataKey="inStockQty" fill="#1677ff" />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Card>
                </Col>
                <Col span={10}>
                  <Card title={<Tooltip title="含待上架。各批次占已入库双数的比例。"><span>已入库批次占比</span></Tooltip>}>
                    {stats.batchDistribution.length === 0 ? (
                      <Empty description="还没有已入库的批次。扫码入库后，这里会按批次显示库龄。" />
                    ) : (
                      <ResponsiveContainer width="100%" height={240}>
                        <PieChart>
                          <Pie data={stats.batchDistribution} dataKey="inStockQty" nameKey="batchNo" outerRadius={80}>
                            {stats.batchDistribution.map((row) => (
                              <Cell key={row.batchNo} fill={PIE_COLORS[stats.batchDistribution.indexOf(row) % PIE_COLORS.length]} />
                            ))}
                          </Pie>
                          <ChartTooltip
                            content={({ active, payload }) => {
                              if (!active || !payload?.[0]) return null;
                              const row = payload[0].payload as DashboardStats["batchDistribution"][number];
                              return <div style={{ background: "#fff", padding: 8 }}>{row.inStockQty} 双 · 库龄 {row.ageDays} 天</div>;
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Card>
                </Col>
              </Row>
              <Card title="最近流转" style={{ marginTop: 16 }}>
                <Table
                  rowKey="id"
                  size="middle"
                  pagination={{ pageSize: 8 }}
                  dataSource={stats.recentLogs}
                  locale={{ emptyText: "还没有流转记录。完成一次入库或复核后，这里会出现。" }}
                  columns={[
                    { title: "时间", dataIndex: "createdAt", render: (value: string) => formatWhen(value) },
                    { title: "做了什么", dataIndex: "title" },
                    { title: "说明", dataIndex: "action", render: (_value: string, row) => row.title },
                    { title: "单双码", dataIndex: "uid" },
                    { title: "操作人", dataIndex: "operator" },
                  ]}
                />
              </Card>
            </>
          ),
        },
        ...(manager
          ? [{
              key: "reports",
              label: "报表",
              children: (
                <>
                  <DatePicker.RangePicker
                    onChange={(values) => {
                      if (!values?.[0] || !values[1]) {
                        setRange(null);
                        return;
                      }
                      setRange([values[0].startOf("day").toISOString(), values[1].endOf("day").toISOString()]);
                    }}
                  />
                  <Button style={{ marginLeft: 8 }} onClick={() => loadReports(range?.[0], range?.[1])}>
                    按这段日期重算
                  </Button>
                  <Card title="拦截统计" style={{ marginTop: 16 }}>
                    <Table
                      rowKey={(row) => `${row.day}-${row.orderNo}`}
                      dataSource={intercepts}
                      locale={{ emptyText: "这段日期里没有记录。换一个日期再查。" }}
                      columns={[
                        { title: "日期", dataIndex: "day" },
                        { title: "出库单号", dataIndex: "orderNo" },
                        { title: "拦截次数", dataIndex: "blocked" },
                        { title: "放行次数", dataIndex: "passed" },
                      ]}
                    />
                  </Card>
                  <Card title="作业量" style={{ marginTop: 16 }}>
                    <Table
                      rowKey={(row) => `${row.day}-${row.operator}`}
                      dataSource={workload}
                      locale={{ emptyText: "这段日期里没有记录。换一个日期再查。" }}
                      columns={[
                        { title: "日期", dataIndex: "day" },
                        { title: "入库双数", dataIndex: "activated" },
                        { title: "上架双数", dataIndex: "putaway" },
                        { title: "复核双数", dataIndex: "verified" },
                        { title: "操作人", dataIndex: "operator" },
                      ]}
                    />
                  </Card>
                  <Card title="对账差异" style={{ marginTop: 16 }}>
                    <Table
                      rowKey="id"
                      dataSource={reconciliations}
                      locale={{ emptyText: "这段日期里没有记录。换一个日期再查。" }}
                      columns={[
                        { title: "周次", dataIndex: "weekKey" },
                        { title: <Tooltip title="含待上架，并加上期初双数。"><span>系统已入库</span></Tooltip>, dataIndex: "systemQty" },
                        { title: "专区实物", dataIndex: "physicalQty" },
                        { title: "Odoo 数量", dataIndex: "odooQty" },
                        { title: "相差双数", render: (_v, row: ReconciliationRow) => `实物 ${row.gapPhysical} / Odoo ${row.gapOdoo}` },
                        { title: "原因", dataIndex: "reason" },
                        { title: "是否已处理完", dataIndex: "resolved", render: (done: boolean) => <Tag color={done ? "success" : "error"}>{done ? "已处理完" : "还没处理完"}</Tag> },
                      ]}
                    />
                  </Card>
                </>
              ),
            }]
          : []),
      ]}
    />
  );
}

function TypographyLine({ title, count }: { title: string; count: number }): JSX.Element {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
      <span>{title}</span>
      <Tag>{count}</Tag>
    </div>
  );
}
